import React, { useEffect, useState } from "react";
import { Building2, Check, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";

/**
 * Which part of the business an AI agent belongs to (§8.10).
 *
 * The AI estate is the one module with no tie to the platform's business spine, which is why AI
 * findings are organization-scoped and a deal-scoped user sees none of them. Assigning an anchor
 * moves this agent's findings — existing ones included — into that deal or business unit on the next
 * scan.
 *
 * Deliberately a human decision. Discovery knows an agent's subscription and its name; it does not
 * know which business process it serves, and a guess here would put findings in front of the wrong
 * team while hiding them from the right one.
 *
 * There are two application entities in the platform — one under Deals, one under Business Units —
 * so the type is chosen explicitly rather than inferred from an id that means different things in
 * each hierarchy.
 */
const TYPES = [
  { value: "", label: "Organization (no specific owner)" },
  { value: "DealApplication", label: "Deal application" },
  { value: "Deal", label: "Deal" },
  { value: "BusinessUnitApplication", label: "Business unit application" },
  { value: "BusinessUnit", label: "Business unit" },
];

/** Where each type's options come from, and how to read an option out of the response. */
const SOURCES = {
  DealApplication: { url: "/Applications", id: "applicationId", name: "applicationName" },
  Deal: { url: "/Deals", id: "dealID", name: "dealName" },
  BusinessUnitApplication: {
    url: "/BusinessUnitApplications", id: "businessUnitApplicationId", name: "applicationName",
  },
  BusinessUnit: { url: "/BusinessUnits", id: "businessUnitId", name: "businessUnitName" },
};

/** Shown for an asset with no anchor, or one whose anchor no longer resolves. */
const NO_OWNER = TYPES[0].label;

/**
 * `businessAnchorName` is the server-resolved name of the anchor (the asset detail's
 * `businessAnchorName`). A caller who cannot manage sees it as text: no dropdowns and no option
 * fetches (AX-268).
 */
export default function AiAssetBusinessAnchor({ asset, businessAnchorName, canManage, onChanged }) {
  const apiCall = useApi();
  const [type, setType] = useState(asset?.businessAnchorType ?? "");
  const [anchorId, setAnchorId] = useState(asset?.businessAnchorId ?? "");
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  // Re-sync when the panel switches asset, or the previous asset's anchor lingers in the form.
  useEffect(() => {
    setType(asset?.businessAnchorType ?? "");
    setAnchorId(asset?.businessAnchorId ?? "");
    setNotice(null);
  }, [asset?.id, asset?.businessAnchorType, asset?.businessAnchorId]);

  useEffect(() => {
    const source = canManage ? SOURCES[type] : null;
    if (!source) { setOptions([]); return; }

    let cancelled = false;
    (async () => {
      setLoadingOptions(true);
      try {
        const result = await apiCall("GET", source.url);
        const rows = Array.isArray(result) ? result : (result?.items ?? result?.data ?? []);
        if (!cancelled) {
          setOptions(rows.map((r) => ({ id: r[source.id], name: r[source.name] ?? `#${r[source.id]}` })));
        }
      } catch {
        // An empty list and a failed load look identical in a dropdown, so say which happened —
        // otherwise "there are no deals" and "we could not fetch deals" are the same screen.
        if (!cancelled) { setOptions([]); setNotice({ tone: "warn", text: "Could not load the list." }); }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, canManage]);

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const body = type
        ? { anchorType: type, anchorId: Number(anchorId) }
        : { anchorType: null, anchorId: null };
      const result = await apiCall("PUT", `/ai-assets/assets/${asset.id}/anchor`, body);
      // The server's message is used rather than a local one: it knows whether the anchor actually
      // resolved, and "saved" alone would look identical when it did not.
      setNotice({ tone: "ok", text: result?.message ?? "Saved." });
      onChanged?.();
    } catch (e) {
      setNotice({
        tone: "warn",
        text: e?.response?.data?.message || e?.message || "Could not save.",
      });
    } finally {
      setSaving(false);
    }
  };

  const current = asset?.businessAnchorType ?? "";
  const dirty = type !== current
    || (!!type && String(anchorId ?? "") !== String(asset?.businessAnchorId ?? ""));

  if (!canManage) {
    return (
      <div className="-mt-1 pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-content-secondary" />
          <span className="text-sm font-semibold text-content-primary">Business owner</span>
        </div>
        <p className="text-sm text-content-primary mt-1" data-testid="business-anchor-name">
          {businessAnchorName ?? NO_OWNER}
        </p>
      </div>
    );
  }

  return (
    <div className="-mt-1 pb-3 border-b border-border">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-content-secondary" />
        <span className="text-sm font-semibold text-content-primary">Business owner</span>
      </div>
      <p className="text-[11px] text-content-secondary mt-0.5">
        Which deal or business unit this agent belongs to. Its findings — including the ones already
        open — move to that scope on the next scan.
      </p>

      <div className="flex flex-wrap items-center gap-2 mt-2">
        <select
          value={type}
          disabled={saving}
          onChange={(e) => { setType(e.target.value); setAnchorId(""); }}
          className="text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
        >
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {type && (
          <select
            value={anchorId ?? ""}
            disabled={saving || loadingOptions}
            onChange={(e) => setAnchorId(e.target.value)}
            className="text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60 max-w-xs"
          >
            <option value="">{loadingOptions ? "Loading…" : "Select…"}</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}

        {dirty && (
          <button
            onClick={save}
            disabled={saving || (!!type && !anchorId)}
            className="px-2 py-1 rounded-md border border-border text-xs text-content-secondary hover:bg-theme-hover disabled:opacity-60 flex items-center gap-1"
          >
            {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Save
          </button>
        )}
      </div>

      {notice && (
        <p className={`text-[11px] mt-2 ${
          notice.tone === "warn" ? "text-amber-600" : "text-content-secondary"
        }`}>
          {notice.text}
        </p>
      )}

      {/* Said once, plainly. Deal privacy is checked before any other access path, so an agent
          anchored to a private deal becomes invisible to people who can see it today — including
          organization-wide risk managers. That is how every other risk type behaves, and it is not
          something anyone would predict from "assign an application to an agent". */}
      {type === "Deal" || type === "DealApplication" ? (
        <p className="text-[11px] text-content-secondary mt-1">
          If the deal is private, this agent's findings become visible only to its members.
        </p>
      ) : null}
    </div>
  );
}
